import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, RefreshCw, Zap, CalendarDays, Infinity, Mail, MessageSquare, Check, ArrowLeft, Send, Users, CreditCard, FileText, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { getFullUrl } from '../lib/siteConfig';
import { sendGmailEmail } from '../lib/email';
import { sendSMS } from '../lib/messaging';
import { Customer } from '../lib/customers';
import SelectCustomerModal from './manage/SelectCustomerModal';
import SubscriptionSendReviewModal from './SubscriptionSendReviewModal';
import CustomerSavedCardPanel from './customer/CustomerSavedCardPanel';
import SubscriptionTaxPreview from './subscriptions/SubscriptionTaxPreview';
import { formatTaxCents, formatTaxRate, quoteSubscriptionTax, SubscriptionTaxRate } from '../lib/subscriptionTax';
import toast from 'react-hot-toast';

interface CreateSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type ModalStep = 'form' | 'send';

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function generateSubscriptionEmailHTML(
  customerFirstName: string,
  businessName: string,
  subscriptionName: string,
  amount: string,
  frequencyLabel: string,
  subscriptionUrl: string,
  customMessage: string,
  logoUrl?: string,
  taxLines: { name: string; rateLabel: string; amount: string }[] = [],
  totalAmount?: string,
): string {
  const safeUrl = escapeHtml(subscriptionUrl);
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Subscription: ${escapeHtml(subscriptionName)}</title>
</head>
<body style="margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;background-color:#F3F4F6;">
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:#F3F4F6;padding:40px 20px;">
    <tr>
      <td align="center">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="max-width:600px;background-color:#FFFFFF;border-radius:8px;overflow:hidden;box-shadow:0 4px 6px rgba(0,0,0,0.1);">

          <!-- Header -->
          <tr>
            <td style="padding:40px 40px 20px;background-color:#FFFFFF;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  ${logoUrl && logoUrl.trim() ? `
                  <td style="vertical-align:middle;width:auto;padding-right:16px;">
                    <img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(businessName)}" style="max-height:40px;max-width:150px;height:auto;display:block;border:0;outline:none;text-decoration:none;" />
                  </td>` : ''}
                  <td style="vertical-align:middle;">
                    <p style="margin:0;font-size:18px;font-weight:600;color:#111827;line-height:1.4;">${escapeHtml(businessName)}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Divider -->
          <tr>
            <td style="padding:0 40px;background-color:#FFFFFF;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr><td style="border-top:1px solid #E5E7EB;height:1px;line-height:1px;font-size:1px;">&nbsp;</td></tr>
              </table>
            </td>
          </tr>

          <!-- Title -->
          <tr>
            <td style="padding:24px 40px 16px;">
              <h1 style="margin:0;font-size:28px;font-weight:bold;color:#111827;letter-spacing:-0.5px;">Subscription</h1>
            </td>
          </tr>

          <!-- Message -->
          <tr>
            <td style="padding:0 40px 24px;">
              <p style="margin:0;font-size:16px;line-height:1.6;color:#374151;white-space:pre-wrap;">${customMessage.replace(/\n/g, '<br>')}</p>
            </td>
          </tr>

          <!-- Subscription Details Card -->
          <tr>
            <td style="padding:0 40px 32px;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:#F9FAFB;border:1px solid #E5E7EB;border-radius:8px;">
                <tr>
                  <td style="padding:20px 24px;">
                    <p style="margin:0 0 4px;font-size:14px;color:#6B7280;font-weight:500;">Subscription</p>
                    <p style="margin:0 0 16px;font-size:18px;font-weight:600;color:#111827;">${escapeHtml(subscriptionName)}</p>
                    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                      <tr>
                        <td style="padding:8px 0;border-top:1px solid #E5E7EB;">
                          <span style="font-size:14px;color:#6B7280;">Amount</span>
                        </td>
                        <td style="padding:8px 0;border-top:1px solid #E5E7EB;text-align:right;">
                          <span style="font-size:16px;font-weight:600;color:#111827;">$${parseFloat(amount).toFixed(2)}</span>
                        </td>
                      </tr>
                      ${taxLines.map((line) => `<tr>
                        <td style="padding:8px 0;border-top:1px solid #E5E7EB;">
                          <span style="font-size:14px;color:#6B7280;">${escapeHtml(line.name)} (${escapeHtml(line.rateLabel)}%)</span>
                        </td>
                        <td style="padding:8px 0;border-top:1px solid #E5E7EB;text-align:right;">
                          <span style="font-size:14px;font-weight:600;color:#111827;">$${escapeHtml(line.amount)}</span>
                        </td>
                      </tr>`).join('')}
                      ${taxLines.length > 0 ? `<tr>
                        <td style="padding:8px 0;border-top:1px solid #E5E7EB;">
                          <span style="font-size:14px;color:#111827;font-weight:600;">Total</span>
                        </td>
                        <td style="padding:8px 0;border-top:1px solid #E5E7EB;text-align:right;">
                          <span style="font-size:16px;font-weight:600;color:#111827;">$${escapeHtml(totalAmount || amount)}</span>
                        </td>
                      </tr>` : ''}
                      <tr>
                        <td style="padding:8px 0;border-top:1px solid #E5E7EB;">
                          <span style="font-size:14px;color:#6B7280;">Frequency</span>
                        </td>
                        <td style="padding:8px 0;border-top:1px solid #E5E7EB;text-align:right;">
                          <span style="font-size:14px;font-weight:600;color:#111827;">${escapeHtml(frequencyLabel)}</span>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- CTA Button -->
          <tr>
            <td style="padding:0 40px 16px;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td align="center">
                    <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center">
                      <tr>
                        <td align="center" style="border-radius:8px;background-color:#2563EB;">
                          <a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="display:block;padding:16px 32px;font-size:16px;font-weight:600;color:#FFFFFF !important;text-decoration:none;border-radius:8px;background-color:#2563EB;min-width:160px;">Review &amp; Activate</a>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Fallback link -->
          <tr>
            <td style="padding:0 40px 32px;">
              <p style="margin:0;font-size:14px;line-height:1.6;color:#6B7280;text-align:center;">
                Or copy this link: <a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="color:#2563EB;text-decoration:underline;word-break:break-all;">${safeUrl}</a>
              </p>
            </td>
          </tr>

          <!-- Sign off -->
          <tr>
            <td style="padding:0 40px 40px;">
              <p style="margin:0;font-size:16px;line-height:1.6;color:#374151;">Sincerely,<br>${escapeHtml(businessName)}</p>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding:30px 40px;background-color:#F9FAFB;border-top:1px solid #E5E7EB;">
              <p style="margin:0;font-size:14px;line-height:1.6;color:#6B7280;text-align:center;">
                This subscription was sent to you by ${escapeHtml(businessName)}. If you have any questions, please contact us directly.
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`.trim();
}

function getFrequencyLabel(frequency: string, customCount?: string, customUnit?: string): string {
  switch (frequency) {
    case 'daily': return 'Daily';
    case 'weekly': return 'Weekly';
    case 'monthly': return 'Monthly';
    case 'every_3_months': return 'Every 3 months';
    case 'every_6_months': return 'Every 6 months';
    case 'custom': {
      const count = parseInt(customCount || '1');
      const units: Record<string, string> = { day: 'days', week: 'weeks', month: 'months', year: 'years' };
      return `Every ${count} ${units[customUnit || 'month'] || 'months'}`;
    }
    default: return 'Monthly';
  }
}

function getStripeInterval(frequency: string): { interval: string; interval_count: number } {
  switch (frequency) {
    case 'daily': return { interval: 'day', interval_count: 1 };
    case 'weekly': return { interval: 'week', interval_count: 1 };
    case 'monthly': return { interval: 'month', interval_count: 1 };
    case 'every_3_months': return { interval: 'month', interval_count: 3 };
    case 'every_6_months': return { interval: 'month', interval_count: 6 };
    default: return { interval: 'month', interval_count: 1 };
  }
}

export default function CreateSubscriptionModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateSubscriptionModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [step, setStep] = useState<ModalStep>('form');
  const [saving, setSaving] = useState(false);

  // Form state
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly' | 'every_3_months' | 'every_6_months' | 'custom'>('monthly');
  const [customIntervalCount, setCustomIntervalCount] = useState('1');
  const [customIntervalUnit, setCustomIntervalUnit] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [billingStart, setBillingStart] = useState<'immediate' | 'custom'>('immediate');
  const [customStartDate, setCustomStartDate] = useState('');
  const [duration, setDuration] = useState<'forever' | 'custom'>('forever');
  const [customEndDate, setCustomEndDate] = useState('');
  const [requireSignature, setRequireSignature] = useState(true);
  const [productType, setProductType] = useState<'system_credit' | null>(null);
  const [collectionMode, setCollectionMode] = useState<'card' | 'invoice'>('card');
  const [taxRates, setTaxRates] = useState<SubscriptionTaxRate[]>([]);
  const [renewalMessageEnabled, setRenewalMessageEnabled] = useState(false);
  const [renewalMessageType, setRenewalMessageType] = useState<'email' | 'sms' | 'both'>('email');
  const [renewalMessageContent, setRenewalMessageContent] = useState('');
  const renewalEditorRef = React.useRef<HTMLDivElement>(null);

  // Send step state
  const [sendMethod, setSendMethod] = useState<'email' | 'sms'>('email');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [createdSubscriptionId, setCreatedSubscriptionId] = useState<string | null>(null);
  const [activationToken, setActivationToken] = useState<string | null>(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [activatingNow, setActivatingNow] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setStep('form');
      setSelectedCustomer(null);
      setName('');
      setDescription('');
      setAmount('');
      setFrequency('monthly');
      setCustomIntervalCount('1');
      setCustomIntervalUnit('month');
      setBillingStart('immediate');
      setCustomStartDate('');
      setDuration('forever');
      setCustomEndDate('');
      setRequireSignature(true);
      setProductType(null);
      setCollectionMode('card');
      setRenewalMessageEnabled(false);
      setRenewalMessageType('email');
      setRenewalMessageContent('');
      setSendMethod('email');
      setCustomerEmail('');
      setCustomerPhone('');
      setCreatedSubscriptionId(null);
      setActivationToken(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 200);
  };

  const handleTransitionEnd = () => {
    if (!isOpen) setIsAnimating(false);
  };

  useEffect(() => {
    if (!isOpen || !selectedBusiness?.id) return;
    let cancelled = false;
    supabase
      .from('tax_rates')
      .select('name, rate')
      .eq('business_id', selectedBusiness.id)
      .then(({ data }) => {
        if (cancelled) return;
        setTaxRates(
          (data || [])
            .map((row) => ({ name: row.name as string, rate: Number(row.rate) || 0 }))
            .filter((row) => row.rate !== 0)
        );
      });
    return () => { cancelled = true; };
  }, [isOpen, selectedBusiness?.id]);

  const taxQuote = quoteSubscriptionTax(parseFloat(amount) || 0, taxRates);
  const amountWithTax = taxQuote.taxCents > 0 ? formatTaxCents(taxQuote.totalCents) : amount;

  // The engine bills a real customer record each cycle — a customer is required.
  const isFormValid = !!selectedCustomer && name.trim() !== '' && amount !== '' && parseFloat(amount) > 0 &&
    (billingStart !== 'custom' || customStartDate !== '') &&
    (duration !== 'custom' || customEndDate !== '') &&
    (frequency !== 'custom' || (parseInt(customIntervalCount) >= 1));

  const handleCreate = async () => {
    if (!selectedBusiness?.id || !isFormValid) return;

    setSaving(true);
    try {
      let interval: string;
      let intervalCount: number;

      if (frequency === 'custom') {
        interval = customIntervalUnit;
        intervalCount = parseInt(customIntervalCount) || 1;
      } else {
        const mapped = getStripeInterval(frequency);
        interval = mapped.interval;
        intervalCount = mapped.interval_count;
      }

      const { data, error } = await supabase
        .from('subscription_services')
        .insert({
          business_id: selectedBusiness.id,
          customer_id: selectedCustomer?.id || null,
          name: name.trim(),
          description: description.trim() || null,
          amount: parseFloat(amount),
          currency: selectedBusiness.currency || 'usd',
          interval,
          interval_count: intervalCount,
          billing_start: billingStart,
          custom_start_date: billingStart === 'custom' ? customStartDate : null,
          duration,
          custom_end_date: duration === 'custom' ? customEndDate : null,
          require_signature: requireSignature,
          product_type: productType,
          collection_mode: collectionMode,
          billing_mode: null,
          renewal_message_enabled: renewalMessageEnabled,
          renewal_message_type: renewalMessageEnabled ? renewalMessageType : null,
          renewal_message_content: renewalMessageEnabled ? renewalMessageContent : null,
          status: 'pending',
        })
        .select('id, activation_token')
        .single();

      if (error) throw error;

      setCreatedSubscriptionId(data.id);
      setActivationToken(data.activation_token);
      if (selectedCustomer?.email) setCustomerEmail(selectedCustomer.email);
      if (selectedCustomer?.phone) setCustomerPhone(selectedCustomer.phone);
      setStep('send');
      toast.success('Subscription created');
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error('Failed to create subscription');
    } finally {
      setSaving(false);
    }
  };

  // Activate on the spot, skipping the customer link. Only possible when no
  // signature is required (the link captures signatures). Card mode also needs
  // a card on file — the server enforces it; the card panel on this step lets
  // staff add one first.
  const handleActivateNow = async () => {
    if (!createdSubscriptionId || !selectedBusiness?.id || !selectedCustomer?.id) return;
    setActivatingNow(true);
    try {
      const { error } = await supabase.rpc('activate_subscription_service', {
        p_subscription_id: createdSubscriptionId,
        p_signature: null,
      });
      if (error) throw new Error(error.message);

      // Billing starts today → run the first cycle right now.
      const { data: run } = await supabase.functions.invoke('subscription-billing', {
        body: { businessId: selectedBusiness.id, customerId: selectedCustomer.id },
      });
      const outcome = Array.isArray(run?.outcomes) ? run.outcomes[run.outcomes.length - 1]?.result : undefined;
      toast.success(
        outcome === 'charged' ? 'Subscription active — first payment charged to the card on file'
          : outcome === 'invoiced' ? 'Subscription active — first invoice issued'
          : outcome === 'charge_failed' ? 'Subscription active, but the first charge failed — a pay link was emailed'
          : 'Subscription activated'
      );
      onSuccess?.();
      handleClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not activate subscription');
    } finally {
      setActivatingNow(false);
    }
  };

  const handleSend = () => {
    if (!createdSubscriptionId) return;
    const contact = sendMethod === 'email' ? customerEmail.trim() : customerPhone.trim();
    if (!contact) {
      toast.error(sendMethod === 'email' ? 'Please enter an email address' : 'Please enter a phone number');
      return;
    }
    setIsReviewModalOpen(true);
  };

  const handleReviewSend = async (data: { subject?: string; message: string }) => {
    if (!createdSubscriptionId || !selectedBusiness?.id || !activationToken) return;

    const contact = sendMethod === 'email' ? customerEmail.trim() : customerPhone.trim();
    const subscriptionUrl = getFullUrl(`/subscribe/${activationToken}`);

    setSending(true);
    try {
      const updateData: any = { send_method: sendMethod };
      if (sendMethod === 'email') {
        updateData.customer_email = contact;
      } else {
        updateData.customer_phone = contact;
      }

      await supabase
        .from('subscription_services')
        .update(updateData)
        .eq('id', createdSubscriptionId);

      if (sendMethod === 'email') {
        let logoUrl: string | undefined;
        try {
          const { data: biz } = await supabase
            .from('businesses')
            .select('booking_settings')
            .eq('id', selectedBusiness.id)
            .single();
          logoUrl = biz?.booking_settings?.logo_url;
        } catch {}

        const freqLabel = getFrequencyLabel(frequency, customIntervalCount, customIntervalUnit);
        const htmlBody = generateSubscriptionEmailHTML(
          selectedCustomer?.first_name || 'there',
          selectedBusiness.name || 'Your service provider',
          name,
          amount,
          freqLabel,
          subscriptionUrl,
          data.message,
          logoUrl,
          taxQuote.lines.map((line) => ({
            name: line.name,
            rateLabel: formatTaxRate(line.rate),
            amount: formatTaxCents(line.amountCents),
          })),
          amountWithTax,
        );

        const emailResult = await sendGmailEmail({
          businessId: selectedBusiness.id,
          to: contact,
          subject: data.subject || `Subscription from ${selectedBusiness.name}`,
          body: htmlBody,
        });

        if (!emailResult.success) {
          throw new Error(emailResult.error || 'Failed to send email');
        }
      } else {
        await sendSMS({
          businessId: selectedBusiness.id,
          to: contact,
          message: data.message,
        });
      }

      toast.success(`Subscription sent via ${sendMethod === 'email' ? 'email' : 'SMS'}`);
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error sending subscription:', error);
      toast.error('Failed to send subscription');
    } finally {
      setSending(false);
    }
  };

  const handleCopyLink = () => {
    if (!activationToken) return;
    const url = getFullUrl(`/subscribe/${activationToken}`);
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const handleSkipSend = () => {
    onSuccess?.();
    handleClose();
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return (<>
    {createPortal(
    <div
      className={`fixed inset-0 transition-opacity duration-200 overflow-x-hidden ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      } z-[100060]`}
      style={{
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={handleClose}
    >
      <div
        className={`bg-white transform transition-all duration-200 max-w-full ${
          isMobile
            ? `fixed left-0 right-0 w-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          ...(isMobile && { left: 0, right: 0 }),
          ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
        }}
        onTransitionEnd={handleTransitionEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden min-w-0 max-w-full">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center">
            {step === 'send' && (
              <button
                onClick={() => setStep('form')}
                className="absolute top-5 left-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-all duration-150 touch-manipulation"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <h2 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
              {step === 'form' ? 'New Subscription' : 'Send to Customer'}
            </h2>
          </div>

          {step === 'form' ? (
            <>
              {/* Form Body */}
              <div
                className="flex-1 overflow-y-auto overflow-x-hidden bg-white min-w-0"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6 min-w-0 max-w-full`}>
                  {/* Customer Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Customer</label>
                    {selectedCustomer ? (
                      <div className="flex items-center p-3 bg-white border border-gray-300 rounded-xl">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-b from-gray-300 to-gray-400 shadow-sm flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                            {selectedCustomer.first_name?.[0]?.toUpperCase()}{selectedCustomer.last_name?.[0]?.toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">
                              {selectedCustomer.first_name} {selectedCustomer.last_name}
                            </div>
                            {selectedCustomer.email && (
                              <div className="text-xs text-gray-500 truncate">{selectedCustomer.email}</div>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedCustomer(null)}
                          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                        >
                          <X className="h-4 w-4 text-gray-400" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsSelectCustomerModalOpen(true)}
                        className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-400 hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                      >
                        <Users className="h-4 w-4" />
                        Select Customer
                      </button>
                    )}
                  </div>

                  {/* Subscription Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Subscription Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Monthly Maintenance Plan"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe what this subscription includes..."
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none"
                    />
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {taxRates.length > 0 ? 'Amount before tax' : 'Amount'}
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      />
                    </div>
                    <SubscriptionTaxPreview amount={amount} rates={taxRates} />
                  </div>

                  {/* Payment Collection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Collection</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setCollectionMode('card')}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          collectionMode === 'card'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <CreditCard className="h-4 w-4 flex-shrink-0" />
                        Card on File
                      </button>
                      <button
                        type="button"
                        onClick={() => setCollectionMode('invoice')}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          collectionMode === 'invoice'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <FileText className="h-4 w-4 flex-shrink-0" />
                        Invoice Only
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {collectionMode === 'card'
                        ? 'A card on file is required to start. Each billing cycle generates an invoice and charges the card automatically — you can add the card yourself or send the customer a link.'
                        : 'No card needed. Each billing cycle generates an invoice on the customer\u2019s payment terms and you collect payment yourself.'}
                    </p>
                  </div>

                  {/* Subscription Product */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Subscription Product</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setProductType(null)}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          productType === null
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        None
                      </button>
                      <button
                        type="button"
                        onClick={() => setProductType('system_credit')}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          productType === 'system_credit'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        System Credit
                      </button>
                    </div>
                    {productType === 'system_credit' && (
                      <p className="text-xs text-gray-500 mt-2">
                        Each billing cycle will automatically add a credit to the customer's wallet equal to the billed amount.
                      </p>
                    )}
                  </div>

                  {/* Frequency */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Frequency</label>
                    <div className="grid grid-cols-3 gap-2">
                      {([
                        { value: 'daily', label: 'Daily' },
                        { value: 'weekly', label: 'Weekly' },
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'every_3_months', label: 'Every 3 Months' },
                        { value: 'every_6_months', label: 'Every 6 Months' },
                        { value: 'custom', label: 'Custom' },
                      ] as const).map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setFrequency(opt.value)}
                          className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-all text-center ${
                            frequency === opt.value
                              ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {frequency === 'custom' && (
                      <div className="flex items-center gap-3 mt-3">
                        <span className="text-sm text-gray-600 font-medium flex-shrink-0">Every</span>
                        <input
                          type="number"
                          value={customIntervalCount}
                          onChange={(e) => setCustomIntervalCount(e.target.value)}
                          min="1"
                          max="156"
                          className="w-20 px-3 py-2.5 border border-gray-300 rounded-xl text-base text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                        />
                        <select
                          value={customIntervalUnit}
                          onChange={(e) => setCustomIntervalUnit(e.target.value as 'day' | 'week' | 'month' | 'year')}
                          className="flex-1 px-3 py-2.5 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                        >
                          <option value="day">Days</option>
                          <option value="week">Weeks</option>
                          <option value="month">Months</option>
                          <option value="year">Years</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Bill Starting */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Bill Starting</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => { setBillingStart('immediate'); setCustomStartDate(''); }}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          billingStart === 'immediate'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Zap className="h-4 w-4 flex-shrink-0" />
                        Immediate
                      </button>
                      <button
                        type="button"
                        onClick={() => setBillingStart('custom')}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          billingStart === 'custom'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <CalendarDays className="h-4 w-4 flex-shrink-0" />
                        Custom Date
                      </button>
                    </div>
                    {billingStart === 'custom' && (
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full mt-3 px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      />
                    )}
                  </div>

                  {/* Duration */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Duration</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => { setDuration('forever'); setCustomEndDate(''); }}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          duration === 'forever'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Infinity className="h-4 w-4 flex-shrink-0" />
                        Forever
                      </button>
                      <button
                        type="button"
                        onClick={() => setDuration('custom')}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          duration === 'custom'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <CalendarDays className="h-4 w-4 flex-shrink-0" />
                        Custom Date
                      </button>
                    </div>
                    {duration === 'custom' && (
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        min={billingStart === 'custom' && customStartDate ? customStartDate : new Date().toISOString().split('T')[0]}
                        className="w-full mt-3 px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      />
                    )}
                  </div>

                  {/* Require Signature */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Require Signature</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setRequireSignature(true)}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          requireSignature
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        onClick={() => setRequireSignature(false)}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          !requireSignature
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </div>

                  {/* Subscription Renewal Message */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-semibold text-gray-700">Subscription Renewal Message</label>
                      <button
                        type="button"
                        onClick={() => setRenewalMessageEnabled(!renewalMessageEnabled)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          renewalMessageEnabled ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          renewalMessageEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                    {renewalMessageEnabled && (
                      <div className="space-y-3 mt-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-2">Send via</label>
                          <div className="grid grid-cols-3 gap-2">
                            {(['email', 'sms', 'both'] as const).map((type) => (
                              <button
                                key={type}
                                type="button"
                                onClick={() => setRenewalMessageType(type)}
                                className={`px-3 py-2 rounded-xl border text-xs font-medium transition-all ${
                                  renewalMessageType === type
                                    ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {type === 'email' ? 'Email' : type === 'sms' ? 'SMS' : 'Both'}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-2">Message</label>
                          <div
                            ref={renewalEditorRef}
                            contentEditable
                            suppressContentEditableWarning
                            onInput={() => {
                              if (renewalEditorRef.current) {
                                setRenewalMessageContent(renewalEditorRef.current.innerHTML);
                              }
                            }}
                            className="w-full min-h-[120px] px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all prose prose-sm max-w-none"
                            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '16px' }}
                            data-placeholder="Enter renewal message for the customer..."
                          />
                          <style>{`
                            [data-placeholder]:empty:before {
                              content: attr(data-placeholder);
                              color: #9ca3af;
                              pointer-events: none;
                            }
                          `}</style>
                          <div className="flex items-center gap-1 mt-2">
                            <button
                              type="button"
                              onClick={() => document.execCommand('bold')}
                              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                              title="Bold"
                            >
                              <span className="text-xs font-bold">B</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => document.execCommand('italic')}
                              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                              title="Italic"
                            >
                              <span className="text-xs italic">I</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => document.execCommand('underline')}
                              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                              title="Underline"
                            >
                              <span className="text-xs underline">U</span>
                            </button>
                            <div className="w-px h-4 bg-gray-300 mx-1" />
                            <button
                              type="button"
                              onClick={() => document.execCommand('insertUnorderedList')}
                              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                              title="Bullet List"
                            >
                              <span className="text-xs">•&ensp;List</span>
                            </button>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            This message will be sent to the customer each time the subscription renews.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Form Footer */}
              <div className="border-t border-gray-200 px-6 py-4 bg-white flex gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors active:scale-[0.98] touch-manipulation"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!isFormValid || saving}
                  className={`flex-1 px-4 py-3 bg-blue-500 text-white rounded-xl text-sm font-semibold transition-all active:scale-[0.98] touch-manipulation ${
                    !isFormValid || saving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'
                  }`}
                >
                  {saving ? 'Creating...' : 'Create Subscription'}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Send Step Body */}
              <div
                className="flex-1 overflow-y-auto overflow-x-hidden bg-white min-w-0"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6 min-w-0 max-w-full`}>
                  {/* Success Banner */}
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-green-800">Subscription created</p>
                        <p className="text-xs text-green-600 mt-0.5">
                          {requireSignature
                            ? 'A signature is required — send the activation link to your customer'
                            : collectionMode === 'card'
                              ? 'Add the customer\u2019s card and start it now, or send them the link'
                              : 'Start it now, or send the customer the activation link'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Start it now — no signature needed, so staff can activate
                      directly (card mode needs a card on file first). */}
                  {!requireSignature && selectedCustomer?.id && selectedBusiness?.id && (
                    <div className="space-y-3">
                      <label className="block text-sm font-semibold text-gray-700">Start it now</label>
                      {collectionMode === 'card' && (
                        <CustomerSavedCardPanel
                          businessId={selectedBusiness.id}
                          customerId={selectedCustomer.id}
                          stripeConnectReady={Boolean(
                            selectedBusiness.stripe_connect_account_id &&
                            selectedBusiness.stripe_connect_charges_enabled &&
                            selectedBusiness.online_payments_enabled !== false,
                          )}
                          customerEmail={selectedCustomer.email}
                          customerName={`${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() || undefined}
                        />
                      )}
                      <button
                        onClick={handleActivateNow}
                        disabled={activatingNow}
                        className={`w-full px-4 py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold transition-all active:scale-[0.98] touch-manipulation flex items-center justify-center gap-2 ${
                          activatingNow ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'
                        }`}
                      >
                        {activatingNow ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                        {activatingNow
                          ? 'Activating…'
                          : billingStart === 'immediate'
                            ? collectionMode === 'card' ? 'Activate & charge first payment' : 'Activate & issue first invoice'
                            : 'Activate — billing starts ' + (customStartDate || 'later')}
                      </button>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-xs text-gray-400 font-medium">or send the link</span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>
                    </div>
                  )}

                  {/* Send Method */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Send via</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setSendMethod('email')}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          sendMethod === 'email'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Mail className="h-4 w-4 flex-shrink-0" />
                        Email
                      </button>
                      <button
                        type="button"
                        onClick={() => setSendMethod('sms')}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                          sendMethod === 'sms'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <MessageSquare className="h-4 w-4 flex-shrink-0" />
                        SMS
                      </button>
                    </div>
                  </div>

                  {/* Contact Input */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {sendMethod === 'email' ? 'Customer Email' : 'Customer Phone'}
                    </label>
                    {sendMethod === 'email' ? (
                      <input
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        placeholder="customer@example.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      />
                    ) : (
                      <input
                        type="tel"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        placeholder="+1 (555) 000-0000"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      />
                    )}
                  </div>

                  {/* Copy Link */}
                  {activationToken && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Or copy the link</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          readOnly
                          value={getFullUrl(`/subscribe/${activationToken}`)}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-base bg-gray-50 text-gray-600 outline-none truncate"
                        />
                        <button
                          onClick={handleCopyLink}
                          className="px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex-shrink-0"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Send Footer */}
              <div className="border-t border-gray-200 px-6 py-4 bg-white flex gap-3">
                <button
                  onClick={handleSkipSend}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors active:scale-[0.98] touch-manipulation"
                >
                  Skip
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending || (sendMethod === 'email' ? !customerEmail.trim() : !customerPhone.trim())}
                  className={`flex-1 px-4 py-3 bg-blue-500 text-white rounded-xl text-sm font-semibold transition-all active:scale-[0.98] touch-manipulation flex items-center justify-center gap-2 ${
                    sending || (sendMethod === 'email' ? !customerEmail.trim() : !customerPhone.trim())
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-blue-600'
                  }`}
                >
                  <Send className="h-4 w-4" />
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  )}

    <SelectCustomerModal
      isOpen={isSelectCustomerModalOpen}
      onClose={() => setIsSelectCustomerModalOpen(false)}
      onSelect={(customer) => {
        setSelectedCustomer(customer);
        setIsSelectCustomerModalOpen(false);
      }}
    />

    <SubscriptionSendReviewModal
      isOpen={isReviewModalOpen}
      onClose={() => setIsReviewModalOpen(false)}
      onSend={handleReviewSend}
      sendMethod={sendMethod}
      customerContact={sendMethod === 'email' ? customerEmail : customerPhone}
      customerFirstName={selectedCustomer?.first_name || 'there'}
      businessName={selectedBusiness?.name || ''}
      subscriptionName={name}
      amount={amountWithTax}
      includesTax={taxQuote.taxCents > 0}
      frequency={frequency === 'custom' ? `custom_${customIntervalCount}_${customIntervalUnit}` : frequency}
      activationToken={activationToken || ''}
    />
  </>);
}
